import { usePersistedDataStore, useCompStore } from '../../store';
import { createRows } from '../services/rowConstructionService';
import { PythonBackendToFrontend, specialFormatters } from '../../../common/mappings';
import logger from '../../../utils/logger';
import {
  findCompIndex,
  getCompIdAtIndex,
  mapCompIds,
} from '../common/compSheetLookup.js';

/**
 * Default comp limit by report type when not set in advanced settings.
 * Assessment: 20 comps; other run types (e.g. sales/standard): 10 comps.
 */
export const getDefaultCompLimitForReportType = (reportType) =>
  reportType?.toLowerCase() === 'assessment' ? 10 : 5;

/**
 * Determine comp limit - unified logic in one place.
 * Priority: 1) providedCompLimit, 2) advancedSettings.compLimit, 3) dynamic default by report type.
 * @param {number|null} providedCompLimit - Explicitly provided comp limit
 * @param {Object} advancedSettings - Advanced settings from localStorage
 * @param {string} reportType - Report type (from negotiationObj); used for default when compLimit not in advanced settings
 * @returns {number} - The determined comp limit
 */
export const determineCompLimit = (providedCompLimit = null, advancedSettings = {}, reportType = 'standard') => {
  let compLimit = providedCompLimit;
  if (compLimit === null || compLimit === undefined) {
    if (advancedSettings?.compLimit !== null && advancedSettings?.compLimit !== undefined && advancedSettings?.compLimit !== '') {
      compLimit = advancedSettings.compLimit;
    } else {
      compLimit = getDefaultCompLimitForReportType(reportType);
    }
  }
  return compLimit;
};

/**
 * Normalize existing saved comps (Comps object or JSON string) to ordered array of parcel IDs.
 */
const existingSavedCompsToPidArray = (existingSavedComps) => {
  if (!existingSavedComps) return [];
  const raw = typeof existingSavedComps === 'string' ? (() => { try { return JSON.parse(existingSavedComps); } catch { return {}; } })() : existingSavedComps;
  if (Array.isArray(raw)) return raw.filter(pid => pid != null && pid !== '');
  if (typeof raw !== 'object') return [];
  return Object.entries(raw)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, v]) => v)
    .filter(pid => pid != null && pid !== '');
};

/**
 * Build expanded comp list: KEEP current saved comps in order, then append the LOWEST unsaved comp from optimized, then the next lowest, etc., until compLimit.
 * @param {Object|Array|string} existingSavedComps - Current saved comps (Comps object, array of IDs, or JSON string)
 * @param {Object} optimizedCase - Optimized case with parcel_id (subject at 0, comps from 1)
 * @param {number} compLimit - Max total comps to return
 * @param {string} reportType - assessment → parcel_id; otherwise TransactionID
 * @returns {Array} - Ordered array of saved comp IDs
 */
export const buildExpandedComps = (existingSavedComps, optimizedCase, compLimit, reportType = 'standard') => {
  const existingArr = existingSavedCompsToPidArray(existingSavedComps);
  if (!optimizedCase?.parcel_id?.length) {
    const out = existingArr.slice(0, compLimit);
    // logger.log('[saveComps:indexes]', 'buildExpandedComps', 'no_optimized_parcel_id', {
    //   compLimit,
    //   existingLen: existingArr.length,
    //   existingArr: [...existingArr],
    //   resultLen: out.length,
    //   result: [...out]
    // });
    return out;
  }
  const optimizedIds = [];
  for (let i = 1; i < optimizedCase.parcel_id.length; i++) {
    const id = getCompIdAtIndex(optimizedCase, i, reportType);
    if (id != null && id !== '') optimizedIds.push(id);
  }
  const savedSet = new Set(existingArr.map((id) => String(id)));
  const unsavedInOptimizedOrder = optimizedIds.filter((id) => !savedSet.has(String(id)));
  const result = [...existingArr];
  for (let i = 0; i < unsavedInOptimizedOrder.length && result.length < compLimit; i++) {
    result.push(unsavedInOptimizedOrder[i]);
  }
  const sliced = result.slice(0, compLimit);
  // logger.log('[saveComps:indexes]', 'buildExpandedComps', 'built', {
  //   compLimit,
  //   subjectAt0: optimizedCase.parcel_id[0],
  //   fullOptimizedParcel_idLen: optimizedCase.parcel_id.length,
  //   fullOptimizedParcel_id: [...optimizedCase.parcel_id],
  //   optimizedCompsOnlyLen: optimizedIds.length,
  //   existingArr: [...existingArr],
  //   unsavedInOptimizedOrderPreview: unsavedInOptimizedOrder.slice(0, 30),
  //   unsavedCount: unsavedInOptimizedOrder.length,
  //   resultLen: sliced.length,
  //   result: [...sliced]
  // });
  return sliced;
};

/**
 * Creates a standardized save object for comp saving operations
 * Centralizes the logic for constructing compsObject, selectedIndexRegular, and selectedIndexOptimized
 * Works for both manual saves (with selectedComps) and automated saves (with optimizedCase)
 * 
 * @param {Object} params - Save object parameters
 * @param {Array|Object} params.selectedComps - Selected comps (array of parcel IDs or array of objects with parcel_id) - for manual saves
 * @param {Object} params.optimizedCase - Optimized case object with parcel_id array - for automated saves (alternative to selectedComps)
 * @param {number} params.caseIndex - Case index to look up comps in store (required)
 * @param {Object} params.subjectRow - Subject row data (optional, for manual saves)
 * @param {number} params.compLimit - Maximum number of comps to save (optional; default by report type: 20 assessment, 10 others)
 * @param {Object} params.regularCompObject - Regular comp object to use directly (optional, for autosave when store may not be updated yet)
 * @returns {Object} - Save data object with compsObject, selectedIndexRegular, selectedIndexOptimized, address, repId
 */
export const createSaveObject = ({
  selectedComps,
  optimizedCase,
  caseIndex,
  subjectRow = null,
  compLimit: providedCompLimit = null,
  regularCompObject = null
}) => {
  // Get compSheet and optimizedComps directly from store, but allow override via regularCompObject parameter
  const getCompSheet = usePersistedDataStore.getState().compSheet || [];
  const getOptimizedComps = usePersistedDataStore.getState().optimizedComps || [];
  const getAdjustments = useCompStore.getState().adjustments;
  const getReportType = usePersistedDataStore.getState().reportType;
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  // const logIdx = (phase, data) => logger.log('[saveComps:indexes]', 'createSaveObject', phase, data);
  const logIdx = () => {};

  const optimizedFromStore = getOptimizedComps[caseIndex];
  const storeOptPid = optimizedFromStore?.parcel_id;
  const paramOptPid = optimizedCase?.parcel_id;
  let storeParamSameOrder = null;
  if (storeOptPid?.length && paramOptPid?.length) {
    const maxL = Math.max(storeOptPid.length, paramOptPid.length);
    storeParamSameOrder = true;
    for (let i = 0; i < maxL; i++) {
      if (storeOptPid[i] !== paramOptPid[i]) {
        storeParamSameOrder = false;
        break;
      }
    }
  }
  let firstStoreParamMismatch = null;
  if (storeOptPid?.length && paramOptPid?.length && storeParamSameOrder === false) {
    const maxL = Math.max(storeOptPid.length, paramOptPid.length);
    for (let i = 0; i < maxL; i++) {
      if (storeOptPid[i] !== paramOptPid[i]) {
        firstStoreParamMismatch = { index: i, storePid: storeOptPid[i], paramPid: paramOptPid[i] };
        break;
      }
    }
  }
  logIdx('sources_snapshot', {
    caseIndex,
    hasParamOptimizedCase: Boolean(optimizedCase),
    hasStoreOptimizedSlot: Boolean(optimizedFromStore),
    storeParcel_idLen: storeOptPid?.length ?? 0,
    paramParcel_idLen: paramOptPid?.length ?? 0,
    storeAndParamParcel_idIdenticalOrder: storeParamSameOrder,
    firstStoreParamMismatch,
    storeParcel_id: storeOptPid ? [...storeOptPid] : null,
    paramParcel_id: paramOptPid ? [...paramOptPid] : null,
    hasRegularInStore: Boolean(getCompSheet[caseIndex]),
    hasRegularParamOverride: Boolean(regularCompObject)
  });

  // Get user filter settings from localStorage (same as CaseReview)
  let userFilterSettings = {};
  let advancedSettings = {};
  try {
    const settings = JSON.parse(localStorage.getItem('advancedSettings')) || {};
    advancedSettings = settings;
    // Override with fresh date range values from negotiationObj if available
    userFilterSettings = {
      ...settings,
      dateRange: getNegotiationObj?.earliestCutoffDate && getNegotiationObj?.latestCutoffDate 
        ? [getNegotiationObj.earliestCutoffDate, getNegotiationObj.latestCutoffDate]
        : settings.dateRange,
      evalDate: getNegotiationObj?.evalDate || settings.evalDate
    };
  } catch (error) {
    logger.error('Error parsing advancedSettings:', error);
  }
  
  // This secondary getreporttype is redundant but will leave for now
  const reportType = getNegotiationObj?.reportType || getReportType;
  const compLimit = determineCompLimit(providedCompLimit, advancedSettings, reportType);
  
  // Determine which comp object to use for row creation
  const compObjectForRows = optimizedCase || getCompSheet[caseIndex];
  if (!compObjectForRows) {
    logIdx('early_exit_no_comp_sheet', {
      caseIndex,
      hadOptimizedCase: Boolean(optimizedCase),
      storeRegularParcel0: getCompSheet[caseIndex]?.parcel_id?.[0] ?? null
    });
    return {
      compsObject: {},
      selectedIndexRegular: [],
      selectedIndexOptimized: [],
      address: subjectRow?.PropertyInfo?.Address || subjectRow?.Address || '',
      repId: null
    };
  }
  
  // Extract subject from comp object (index 0)
  const subject = {
    parcel_id: compObjectForRows.parcel_id?.[0],
    Acres: compObjectForRows.Acres?.[0],
    Sqft: compObjectForRows.Sqft?.[0],
    SalePrice: compObjectForRows.SalePrice?.[0],
    IFMV: compObjectForRows.IFMV?.[0],
    // Include all adjustment fields
    ...Object.fromEntries(
      Object.keys(getAdjustments || {})
        .filter(key => key !== 'manuallyAddedComps' && key !== 'specificPid')
        .map(key => {
          // Map backend key to frontend field name
          const frontendKey = PythonBackendToFrontend[key];
          if (frontendKey && compObjectForRows[frontendKey]) {
            return [key, compObjectForRows[frontendKey]?.[0]];
          }
          return null;
        })
        .filter(Boolean)
    )
  };
  
  // Determine if this is an assessment run
  const IS_SALE_RUN = getReportType?.toLowerCase() !== 'assessment';
  
  // Extract parcel IDs based on input type
  let parcelIds = [];
  
  let parcelIdSource = 'none';

  // If optimizedCase is provided (automated save), extract comps directly
  if (optimizedCase && optimizedCase.parcel_id && Array.isArray(optimizedCase.parcel_id)) {
    parcelIdSource = 'param_optimizedCase_slice';
    // For automated saves, take comps up to compLimit
    parcelIds = [];
    for (let i = 1; i < optimizedCase.parcel_id.length && parcelIds.length < compLimit; i++) {
      const id = getCompIdAtIndex(optimizedCase, i, reportType);
      if (id != null && id !== '') {
        parcelIds.push(id);
      }
    }
  } 
  // Otherwise, use selectedComps (manual save)
  else if (selectedComps) {
    parcelIdSource = 'selectedComps';
    // For manual saves, save all selected comps exactly as the user selected them
    if (Array.isArray(selectedComps)) {
      // Check if it's an array of objects or array of parcel IDs
      const isArrayOfObjects = selectedComps.every(item => typeof item === 'object' && item !== null);
      
      if (isArrayOfObjects) {
        parcelIds = selectedComps.map(item => item.parcel_id || item.ParcelID || item);
      } else {
        parcelIds = selectedComps;
      }
    } else if (typeof selectedComps === 'object') {
      // If it's an object (like compsObject format), extract values
      parcelIds = Object.values(selectedComps);
    }
    
    // Filter out any null/undefined/empty values only
    parcelIds = parcelIds.filter(pid => pid != null && pid !== '');
    // Sales: UI selection is parcel_id; persist TransactionID
    const sheetForIds = regularCompObject || getCompSheet[caseIndex] || optimizedCase;
    parcelIds = mapCompIds(sheetForIds, parcelIds, reportType);
  }

  logIdx('parcel_ids_after_extract', {
    parcelIdSource,
    compLimit,
    count: parcelIds.length,
    parcelIds: [...parcelIds]
  });

  // If no valid parcel IDs found, return empty save object
  if (!parcelIds || parcelIds.length === 0) {
    // Extract repId from optimizedCase if available (for automated saves)
    let repId = null;
    if (optimizedCase) {
      if (optimizedCase?.RepID?.[0] && optimizedCase.RepID[0] !== '') {
        repId = optimizedCase.RepID[0];
      } else if (optimizedCase?.RepId && optimizedCase.RepId !== '') {
        repId = optimizedCase.RepId;
      }
    } else if (subjectRow) {
      if (subjectRow?.RepId && subjectRow.RepId !== '') {
        repId = subjectRow.RepId;
      } else if (subjectRow?.RepID && subjectRow.RepID !== '') {
        repId = subjectRow.RepID;
      }
    }

    logIdx('early_exit_no_parcel_ids', {
      caseIndex,
      hadOptimizedCase: Boolean(optimizedCase),
      hadSelectedComps: Boolean(selectedComps),
      parcelIdSource
    });
    return {
      compsObject: {},
      selectedIndexRegular: [],
      selectedIndexOptimized: [],
      address: subjectRow?.PropertyInfo?.Address || subjectRow?.Address || '',
      repId
    };
  }

  // Construct compsObject with sequential keys starting from 1
  const compsObject = {};
  parcelIds.forEach((pid, index) => {
    compsObject[index + 1] = pid;
  });

  // Create rows for index calculation
  // We need separate rows for regular and optimized to get the correct arrays
  
  // For regular: create rows from regular compSheet
  // Use provided regularCompObject if available (for autosave when store may not be updated), otherwise use store
  const regularCompObjectForRows = regularCompObject || getCompSheet[caseIndex];
  const regularRows = regularCompObjectForRows ? createRows({
    comps: regularCompObjectForRows,
    view: 'regular',
    savedComps: [],
    selectedComps: [],
    getNegotiationObj,
    village: null,
    subject,
    IS_SALE_RUN,
    compAdjPriceField: IS_SALE_RUN ? 'adj_price' : 'adj_price_IFMV',
    specialFormatters,
    handleNumericValue: (value, defaultValue = '') => {
      if (value === 0 || value === '0') return 0;
      return value || defaultValue;
    },
    getAdjustments,
    userFilterSettings
  }) : [];
  
  // For optimized: create rows from optimized comps (use optimizedCase if available, otherwise from store)
  const optimizedCompObject = optimizedCase || getOptimizedComps[caseIndex];
  const indexingUsesParamOptimized = Boolean(optimizedCase && optimizedCompObject === optimizedCase);
  logIdx('objects_for_optimized_indexing', {
    indexingUsesParamOptimized,
    indexingUsesStoreOnly: Boolean(!optimizedCase && optimizedCompObject === optimizedFromStore),
    chosenIsParam: optimizedCompObject === optimizedCase,
    chosenIsStore: optimizedCompObject === optimizedFromStore,
    indexingParcel_idLen: optimizedCompObject?.parcel_id?.length ?? 0,
    indexingParcel_id: optimizedCompObject?.parcel_id ? [...optimizedCompObject.parcel_id] : null,
    storeParcel_idWhenDiffers:
      indexingUsesParamOptimized && storeOptPid && optimizedCompObject?.parcel_id
        ? [...storeOptPid]
        : null
  });

  const regularStorePid = getCompSheet[caseIndex]?.parcel_id;
  const regularUsedPid = regularCompObjectForRows?.parcel_id;
  logIdx('objects_for_regular_indexing', {
    regularUsesParamOverride: Boolean(regularCompObject),
    regularParcel_idLen: regularUsedPid?.length ?? 0,
    regularParcel_id: regularUsedPid ? [...regularUsedPid] : null,
    storeRegularParcel_idWhenOverride:
      regularCompObject && regularStorePid ? [...regularStorePid] : null
  });

  const optimizedRows = optimizedCompObject ? createRows({
    comps: optimizedCompObject,
    view: 'optimized',
    savedComps: [],
    selectedComps: [],
    getNegotiationObj,
    village: null,
    subject,
    IS_SALE_RUN,
    compAdjPriceField: IS_SALE_RUN ? 'adj_price' : 'adj_price_IFMV',
    specialFormatters,
    handleNumericValue: (value, defaultValue = '') => {
      if (value === 0 || value === '0') return 0;
      return value || defaultValue;
    },
    getAdjustments,
    userFilterSettings
  }) : [];

  // This is stupid and redundant, we then add +1 back to the index later, without filtering (assumgin you only ever filter subj this is just not needed)
  // Create arrays from the respective row sets (for fallback index calculation)
  const filteredRegularParcelIds = regularRows
    .filter(row => !row.isSubject)
    .map(row => row.parcel_id || row.id)
    .filter(pid => pid != null && pid !== '');
  
  const filteredOptimizedParcelIds = optimizedRows
    .filter(row => !row.isSubject)
    .map(row => row.parcel_id || row.id)
    .filter(pid => pid != null && pid !== '');

  // Calculate selectedIndexRegular
  // Find the original position in regularCompObjectForRows (subject is at 0, comps start at 1)
  const selectedIndexRegular = Object.entries(compsObject)
    .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
    .map(([_, pid]) => {
      if (!regularCompObjectForRows || !regularCompObjectForRows.parcel_id || !Array.isArray(regularCompObjectForRows.parcel_id)) {
        // Fallback to filtered array lookup if regularCompObject is not available
        if (!filteredRegularParcelIds || !Array.isArray(filteredRegularParcelIds)) {
          return null;
        }
        const index = filteredRegularParcelIds.indexOf(pid);
        if (index === -1) return null;
        return index + 1;
      }
      const originalIndex = findCompIndex(regularCompObjectForRows, pid, reportType);
      if (originalIndex === -1) return null;
      return originalIndex;
    });

  // Calculate selectedIndexOptimized
  const selectedIndexOptimized = Object.entries(compsObject)
    .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
    .map(([_, pid]) => {
      if (!optimizedCompObject || !optimizedCompObject.parcel_id || !Array.isArray(optimizedCompObject.parcel_id)) {
        if (!filteredOptimizedParcelIds || !Array.isArray(filteredOptimizedParcelIds)) {
          return null;
        }
        const index = filteredOptimizedParcelIds.indexOf(pid);
        if (index === -1) return null;
        return index + 1;
      }
      const originalIndex = findCompIndex(optimizedCompObject, pid, reportType);
      if (originalIndex === -1) return null;
      return originalIndex;
    });

  const sortedCompEntries = Object.entries(compsObject).sort(([keyA], [keyB]) => Number(keyA) - Number(keyB));
  const optimizedPerComp = sortedCompEntries.map(([compKey, pid], ord) => {
    const full = optimizedCompObject?.parcel_id;
    let branch = 'none';
    let indexFromFull = null;
    if (full?.length) {
      const ix = findCompIndex(optimizedCompObject, pid, reportType);
      indexFromFull = ix >= 0 ? ix : null;
      branch = ix >= 0 ? 'findCompIndex' : 'not_in_sheet';
    } else if (filteredOptimizedParcelIds?.length) {
      const fi = filteredOptimizedParcelIds.indexOf(pid);
      branch = fi >= 0 ? 'filteredOptimizedRows' : 'not_in_filtered_rows';
    } else {
      branch = 'no_optimized_object';
    }
    return {
      ord,
      compKey,
      pid,
      branch,
      indexFromFullArray: indexFromFull,
      selectedIndexOptimizedResult: selectedIndexOptimized[ord],
      selectedIndexRegularResult: selectedIndexRegular[ord]
    };
  });
  logIdx('per_comp_index_breakdown', {
    filteredOptimizedRowPids: [...filteredOptimizedParcelIds],
    filteredRegularRowPids: [...filteredRegularParcelIds],
    optimizedPerComp
  });

  // Extract address from subjectRow if available (only for manual saves)
  const address = subjectRow?.PropertyInfo?.Address || subjectRow?.Address || '';

  // Extract repId - prioritize optimizedCase (automated save), then subjectRow (manual save)
  let repId = null;
  if (optimizedCase) {
    // For automated saves, get repId from optimizedCase
    if (optimizedCase?.RepID?.[0] && optimizedCase.RepID[0] !== '') {
      repId = optimizedCase.RepID[0];
    } else if (optimizedCase?.RepId && optimizedCase.RepId !== '') {
      repId = optimizedCase.RepId;
    }
  } else if (subjectRow) {
    // For manual saves, get repId from subjectRow
    if (subjectRow?.RepId && subjectRow.RepId !== '') {
      repId = subjectRow.RepId;
    } else if (subjectRow?.RepID && subjectRow.RepID !== '') {
      repId = subjectRow.RepID;
    }
  }

  const compsKeysOrdered = Object.keys(compsObject).sort((a, b) => Number(a) - Number(b));
  logIdx('built_summary', {
    caseIndex,
    compLimit,
    parcelIdSource,
    subjectForRowsFrom: optimizedCase && compObjectForRows === optimizedCase ? 'param_optimizedCase' : 'store_compSheet',
    rowSource: optimizedCase ? 'optimizedCase' : 'store_compSheet',
    optimizedSource: optimizedCase ? 'param_optimizedCase' : 'store_optimizedComps',
    compsKeysOrdered,
    compsCount: compsKeysOrdered.length,
    selectedIndexRegular,
    selectedIndexOptimized,
    regLen: selectedIndexRegular?.length,
    optLen: selectedIndexOptimized?.length
  });

  return {
    compsObject,
    selectedIndexRegular,
    selectedIndexOptimized,
    address,
    repId
  };
};
